import { api } from './api.js';

// Plaid Link loader + flow. The app is an ES-module bundle with a strict CSP;
// Link's script is injected lazily from cdn.plaid.com on first use (script-src
// allows that origin) rather than shipped in a static <script>. Used by Settings
// (link a bank, Fix login) and the Home broken-connection banner.

interface PlaidLinkHandler { open(): void; exit(): void; destroy(): void; }
interface PlaidMetadata { institution?: { name?: string } | null; }
interface PlaidLinkConfig {
  token: string;
  onSuccess(publicToken: string, metadata: PlaidMetadata): void;
  onExit(err: PlaidLinkError | null, metadata: unknown): void;
}
interface PlaidLinkError { display_message?: string | null; error_message?: string | null; }
interface PlaidFactory { create(config: PlaidLinkConfig): PlaidLinkHandler; }

declare global {
  interface Window { Plaid?: PlaidFactory; }
}

const LINK_SRC = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
let scriptPromise: Promise<void> | null = null;

function loadPlaidScript(): Promise<void> {
  if (window.Plaid) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = LINK_SRC;
    script.onload = () => resolve();
    script.onerror = () => { scriptPromise = null; reject(new Error('Could not load Plaid Link')); };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export interface OpenPlaidLinkOptions {
  /** Override PLAID_PRODUCTS for this session (e.g. ['investments'] for a brokerage). */
  products?: string[];
  /** Fix login: re-authenticate this existing connection in Plaid's update mode
   *  (no new item, no exchange), then sync it. */
  connectionId?: number;
  /** Called after a successful link + exchange (reload the connection list). */
  onLinked?: () => void | Promise<void>;
  /** Called with a human-readable message on any failure or user-facing exit error. */
  onError?: (message: string) => void;
}

/** Open Plaid Link: fetch a link token → let the user pick + authenticate a bank
 *  → exchange the public token server-side (which stores the encrypted access
 *  token and kicks off the initial sync). Never throws — failures surface via
 *  onError. The returned promise resolves only once the whole flow SETTLES
 *  (success + exchange done, user-cancel, or error), so callers can safely do
 *  post-await work (re-enable a button, reload) without racing the open modal. */
export async function openPlaidLink(opts: OpenPlaidLinkOptions = {}): Promise<void> {
  try {
    await loadPlaidScript();
    const { link_token } = await api.post<{ link_token: string }>(
      '/plaid/link-token',
      opts.connectionId != null ? { connection_id: opts.connectionId }
        : opts.products ? { products: opts.products } : {},
    );
    if (!window.Plaid) throw new Error('Plaid Link unavailable');

    await new Promise<void>((resolve) => {
      const handler = window.Plaid!.create({
        token: link_token,
        onSuccess: (publicToken, metadata) => {
          const finish = opts.connectionId != null
            ? api.post(`/connections/sync/${opts.connectionId}`)
            : api.post('/plaid/exchange', {
              public_token: publicToken,
              institution_name: metadata.institution?.name,
            });
          finish
            .then(() => opts.onLinked?.())
            .catch((err) => opts.onError?.(err instanceof Error ? err.message : 'Could not finish linking'))
            .finally(() => resolve());
        },
        // onExit fires on user-cancel (err null) and on Link errors — either way
        // the flow is over, so resolve. Surface a message only on a real error.
        onExit: (err) => {
          if (err) opts.onError?.(err.display_message || err.error_message || 'Link closed before finishing');
          resolve();
        },
      });
      handler.open();
    });
  } catch (err) {
    opts.onError?.(err instanceof Error ? err.message : 'Could not open Plaid');
  }
}
